import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useApi } from "../../api";
import { esc, n2, printHtml, round2 } from "../../lib/format";
import "./pos.css";
import "./wholesale.css";

/*
 * Mixed crate / carton wholesale POS – port of wholesale_pos.php (API: backend/wholesale.py)
 * Choose a package, fill it with exactly `capacity` bottles, take payment.
 * Per-bottle price = package price ÷ capacity (from Wholesale Price Management).
 * Keys: F2 complete · F3 preview · / search · Esc stop auto-pay
 */

const AUTO_PAY_SECONDS = 3;
const DRAFT_KEY = "wholesale_draft";
const PAYMENT_METHODS = [["Cash", "Cash"], ["LipaNumber", "Lipa"], ["Bank", "Bank"]];
const pkgIcon = (cap) => (cap >= 24 ? "fa-basket-shopping" : cap >= 12 ? "fa-box" : "fa-boxes-stacked");

function receiptHtml({ title, client, date, method, pkg, bottles, lines, total, received, change, preview }) {
  const rows = lines.map((l) => `<tr><td>${esc(l.name)}</td><td class="c">${l.qty}</td><td class="r">${n2(l.package_price)}</td><td class="r">${n2(l.total)}</td></tr>`).join("");
  return `<!doctype html><html><head><meta charset="utf-8"><title>${title}</title><style>
    body{font-family:'Segoe UI',Arial,sans-serif;font-size:12px;margin:0;padding:14px;width:350px;color:#111}
    h3{margin:0;text-align:center;font-size:16px}.s{text-align:center;font-size:11px;color:#555}
    .meta{border-top:1px dashed #bbb;border-bottom:1px dashed #bbb;padding:8px 0;margin:10px 0}
    table{width:100%;border-collapse:collapse}th{text-align:left;border-bottom:1px solid #999;padding-bottom:4px}
    td{padding:5px 4px;border-bottom:1px dashed #ddd}.r{text-align:right}.c{text-align:center}
    tfoot td{border:0}.g{font-weight:700}.ch{font-weight:700;color:#16a34a}
    .end{text-align:center;margin-top:12px;font-size:11px;color:#777;border-top:1px dashed #bbb;padding-top:8px}</style></head><body>
    <h3>AE2C MINI MARKET</h3><div class="s">Super Quality Everyday Needs</div><div class="s">${preview ? "Wholesale Receipt Preview" : "Wholesale Receipt"}</div>
    <div class="meta">${date ? `<div><b>Date:</b> ${esc(date)}</div>` : ""}<div><b>Client:</b> ${esc(client || "–")}</div>
    ${method ? `<div><b>Payment:</b> ${esc(method)}</div>` : ""}<div><b>Package:</b> ${esc(pkg)}</div><div><b>Bottles:</b> ${bottles}</div>
    ${preview ? "<div><b>Note:</b> Prices shown are per-package rates</div>" : ""}</div>
    <table><thead><tr><th>Product</th><th class="c">Qty</th><th class="r">Pkg price</th><th class="r">Total</th></tr></thead><tbody>${rows}</tbody>
    <tfoot><tr class="g"><td colspan="3" class="r">Total</td><td class="r">TZS ${n2(total)}</td></tr>
    ${received != null ? `<tr><td colspan="3" class="r">Received</td><td class="r">TZS ${n2(received)}</td></tr>
    <tr class="ch"><td colspan="3" class="r">Change</td><td class="r">TZS ${n2(change)}</td></tr>` : ""}</tfoot></table>
    <div class="end">${preview ? "*** PREVIEW - NOT A RECEIPT ***" : "Thank you for shopping wholesale with us!"}</div></body></html>`;
}

export default function WholesalePos() {
  const api = useApi();
  const searchRef = useRef(null);
  const [boot, setBoot] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [search, setSearch] = useState("");
  const [client, setClient] = useState(null); // {id, name}
  const [clientQ, setClientQ] = useState("");
  const [clientIdx, setClientIdx] = useState(-1);
  const [method, setMethod] = useState("Cash");
  const [pkg, setPkg] = useState(null);
  const [cart, setCart] = useState([]);
  const [received, setReceived] = useState("");
  const [notice, setNotice] = useState(null);
  const [flash, setFlash] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const [autoPayCancelled, setAutoPayCancelled] = useState(false);
  const [draftOffer, setDraftOffer] = useState(null);

  const load = useCallback(async () => {
    try {
      const d = await api.get("/api/wholesale/pos");
      setBoot(d);
      setLoadError(null);
      return d;
    } catch (e) { setLoadError(e.message); return null; }
  }, [api]);

  useEffect(() => {
    load().then((d) => {
      if (!d) return;
      setClient((c) => c || d.clients[0] || null);
      try {
        const draft = JSON.parse(localStorage.getItem(DRAFT_KEY) || "null");
        if (draft?.cart?.length) setDraftOffer(draft);
      } catch { /* ignore a broken draft */ }
      searchRef.current?.focus();
    });
  }, [load]);

  const getPrice = useCallback((pid, pkgId) => boot?.prices?.[pkgId]?.[pid] || null, [boot]);
  const flashError = useCallback((text) => {
    setFlash(text);
    setTimeout(() => setFlash((f) => (f === text ? null : f)), 2200);
  }, []);

  /* ── derived ── */
  const totalQty = cart.reduce((s, i) => s + i.qty, 0);
  const total = round2(cart.reduce((s, i) => s + i.price_per_bottle * i.qty, 0));
  const receivedNum = parseFloat(received) || 0;
  const cap = pkg?.cap || 0;
  const canCheckout = !!client && !!pkg && totalQty === cap && cart.length > 0 && receivedNum >= total && total > 0 && !submitting;
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = boot?.products || [];
    if (!q) return list;
    return list.filter((p) => (p.name || "").toLowerCase().includes(q) || (p.type || "").toLowerCase().includes(q) || (p.mfr || "").toLowerCase().includes(q));
  }, [boot, search]);
  const clientMatches = useMemo(() => {
    const q = clientQ.trim().toLowerCase();
    if (!q || !boot) return [];
    return boot.clients.filter((c) => c.name.toLowerCase().includes(q)).slice(0, 15);
  }, [boot, clientQ]);
  const fill = !pkg ? null : totalQty === cap ? "full" : totalQty > cap ? "over" : "part";
  const fillText = !pkg ? "" : totalQty === 0 ? `Choose products: ${cap} bottles needed`
    : totalQty < cap ? `${totalQty}/${cap} · ${cap - totalQty} more bottle${cap - totalQty !== 1 ? "s" : ""} needed`
    : totalQty === cap ? "Package full, ready to check out" : `Over by ${totalQty - cap} bottle${totalQty - cap !== 1 ? "s" : ""}`;
  const validation = !pkg && cart.length ? "Select a package type first"
    : pkg && cart.length && totalQty !== cap ? `Package must be exactly ${cap} bottles (currently ${totalQty})`
    : cart.length && total > 0 && receivedNum > 0 && receivedNum < total ? `Need TZS ${n2(total - receivedNum)} more`
    : !client && cart.length ? "Please select a client" : "";

  /* ── package & cart ── */
  const selectPackage = (p) => {
    setPkg(p);
    setCart((c) => {
      let next = c.map((i) => {
        const pp = getPrice(i.id, p.id);
        return pp ? { ...i, package_price: pp.package_price, price_per_bottle: pp.price_per_bottle } : null;
      }).filter(Boolean);
      let qty = next.reduce((s, i) => s + i.qty, 0);
      while (qty > p.cap && next.length) {
        const last = next[next.length - 1];
        next = last.qty > 1 ? [...next.slice(0, -1), { ...last, qty: last.qty - 1 }] : next.slice(0, -1);
        qty -= 1;
      }
      return next;
    });
  };
  const addToCart = (p) => {
    if (!pkg) { flashError("Select a package type first"); return; }
    const pp = getPrice(p.id, pkg.id);
    if (!pp) { flashError("No price set for this product in this package"); return; }
    if (totalQty >= pkg.cap) { flashError("Package is already full"); return; }
    const existing = cart.find((i) => i.id === p.id);
    if (existing) {
      if (existing.qty >= p.stock) { flashError(`Stock limit reached for ${p.name}`); return; }
      setCart(cart.map((i) => (i.id === p.id ? { ...i, qty: i.qty + 1 } : i)));
    } else {
      if (p.stock <= 0) { flashError(`${p.name} is out of stock`); return; }
      setCart([...cart, { id: p.id, name: p.name, mfr: p.mfr, stock: p.stock, qty: 1, package_price: pp.package_price, price_per_bottle: pp.price_per_bottle }]);
    }
  };
  const inc = (idx) => {
    if (totalQty >= cap) { flashError("Package is full"); return; }
    if (cart[idx].qty >= cart[idx].stock) { flashError("Stock limit reached"); return; }
    setCart(cart.map((i, k) => (k === idx ? { ...i, qty: i.qty + 1 } : i)));
  };
  const dec = (idx) => setCart(cart[idx].qty > 1 ? cart.map((i, k) => (k === idx ? { ...i, qty: i.qty - 1 } : i)) : cart.filter((_, k) => k !== idx));
  const clearCart = () => { if (cart.length && window.confirm("Clear all products from the package?")) { setCart([]); setReceived(""); } };
  const cancelSale = () => { if (window.confirm("Cancel this sale and clear all data?")) { setCart([]); setPkg(null); setReceived(""); } };

  /* ── draft ── */
  const saveDraft = () => {
    localStorage.setItem(DRAFT_KEY, JSON.stringify({ client_id: client?.id, payment: method, package: pkg?.id, cart: cart.map((i) => ({ id: i.id, qty: i.qty })), saved: new Date().toISOString() }));
    setNotice({ tone: "ok", text: "Draft saved. It will be offered for restore next time you open this page." });
  };
  const restoreDraft = () => {
    const d = draftOffer;
    setDraftOffer(null);
    localStorage.removeItem(DRAFT_KEY);
    const cl = boot.clients.find((c) => c.id === d.client_id);
    if (cl) setClient(cl);
    setMethod(d.payment || "Cash");
    const p = boot.packages.find((x) => x.id === d.package);
    if (p) setPkg(p);
    setCart((d.cart || []).map((ci) => {
      const pr = boot.products.find((x) => x.id === ci.id);
      const pp = pr && p ? getPrice(pr.id, p.id) : null;
      return pp ? { id: pr.id, name: pr.name, mfr: pr.mfr, stock: pr.stock, qty: ci.qty, package_price: pp.package_price, price_per_bottle: pp.price_per_bottle } : null;
    }).filter(Boolean));
  };

  /* ── preview & sale ── */
  const openPreview = () => {
    if (!cart.length) { flashError("Cart is empty"); return; }
    printHtml(receiptHtml({ title: "Preview", preview: true, client: client?.name, pkg: pkg ? `${pkg.name} (${pkg.cap} bottles)` : "–",
      bottles: totalQty, lines: cart.map((i) => ({ name: i.name, qty: i.qty, package_price: i.package_price, total: i.qty * i.price_per_bottle })), total }));
  };
  const completeSale = async () => {
    if (!canCheckout) return;
    setSubmitting(true); setNotice(null);
    try {
      const { receipt } = await api.post("/api/wholesale/sale", {
        client_id: client.id, payment_method: method, package_id: pkg.id, received_amount: receivedNum,
        items: cart.map((i) => ({ id: i.id, qty: i.qty })),
      });
      printHtml(receiptHtml({ title: "Wholesale Receipt", client: receipt.client, date: receipt.date, method: receipt.payment_method,
        pkg: receipt.package, bottles: receipt.bottles, total: receipt.total, received: receipt.received, change: receipt.change,
        lines: receipt.lines.map((l) => ({ name: l.product_name, qty: l.quantity, package_price: l.package_price, total: l.line_total })) }));
      setNotice({ tone: "ok", text: `Wholesale sale completed for ${receipt.client}. Change: TZS ${n2(receipt.change)}` });
      setCart([]); setReceived("");
      load();
    } catch (e) {
      setNotice({ tone: "err", text: e.message });
      setAutoPayCancelled(true);
    } finally { setSubmitting(false); }
  };

  /* ── auto-pay countdown ── */
  useEffect(() => { setAutoPayCancelled(false); }, [cart, receivedNum, client, pkg]);
  const completeRef = useRef(completeSale);
  completeRef.current = completeSale;
  useEffect(() => {
    if (!AUTO_PAY_SECONDS || !canCheckout || autoPayCancelled) { setCountdown(null); return undefined; }
    let n = AUTO_PAY_SECONDS;
    setCountdown(n);
    const t = setInterval(() => { n -= 1; if (n <= 0) { clearInterval(t); setCountdown(null); completeRef.current(); } else setCountdown(n); }, 1000);
    return () => clearInterval(t);
  }, [canCheckout, autoPayCancelled, cart, receivedNum]);

  /* ── keyboard ── */
  const keys = useRef({});
  keys.current = { completeSale, openPreview, countdown };
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target.tagName || "").toLowerCase();
      const typing = tag === "input" || tag === "textarea" || tag === "select";
      if (e.key === "F2") { e.preventDefault(); keys.current.completeSale(); return; }
      if (e.key === "F3") { e.preventDefault(); keys.current.openPreview(); return; }
      if (e.key === "Escape") { if (keys.current.countdown != null) setAutoPayCancelled(true); setClientQ(""); return; }
      if (e.key === "/" && !typing) { e.preventDefault(); searchRef.current?.focus(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const pickClient = (c) => { setClient(c); setClientQ(""); setClientIdx(-1); };
  const avgPkgPrice = (pkgId) => {
    const prices = cart.map((i) => getPrice(i.id, pkgId)?.package_price || 0).filter((v) => v > 0);
    return prices.length ? prices.reduce((a, b) => a + b, 0) / prices.length : 0;
  };

  return (
    <main className="pos-page">
      <div className="pos-head">
        <div><h1>Wholesale sales</h1><p>Mixed crates and cartons at package prices</p></div>
        <div className="keys"><span><kbd>F2</kbd>Pay</span><span><kbd>F3</kbd>Preview</span><span><kbd>/</kbd>Search</span><span><kbd>Esc</kbd>Stop auto-pay</span>
          <Link to="/wholesale_prices" className="btn sm"><i className="fas fa-tag" /> Prices</Link></div>
      </div>

      {notice && <div className={`pos-notice ${notice.tone}`} role={notice.tone === "err" ? "alert" : "status"}><span>{notice.text}</span>
        <button type="button" aria-label="Dismiss" onClick={() => setNotice(null)}><i className="fas fa-xmark" /></button></div>}
      {loadError && <div className="pos-notice err" role="alert"><span>Couldn't load: {loadError}</span><button type="button" className="btn" onClick={load}>Retry</button></div>}
      {draftOffer && (
        <div className="pos-notice ok" role="status">
          <span>A saved draft from {new Date(draftOffer.saved).toLocaleString("en-GB")} is available.</span>
          <span style={{ display: "flex", gap: ".4rem" }}>
            <button type="button" className="btn sm primary" onClick={restoreDraft}>Restore</button>
            <button type="button" className="btn sm" onClick={() => { setDraftOffer(null); localStorage.removeItem(DRAFT_KEY); }}>Discard</button>
          </span>
        </div>
      )}

      <div className="ws-grid">
        {/* ── left: client, package, products ── */}
        <div className="ws-left">
          <section className="panel ws-box">
            <h2>Client and payment</h2>
            <div className="ws-client">
              <label htmlFor="ws-client">Client</label>
              <div className="ws-client-box">
                <input id="ws-client" value={clientQ} placeholder={client ? client.name : "Type client name"} autoComplete="off"
                  onChange={(e) => { setClientQ(e.target.value); setClientIdx(-1); }}
                  onKeyDown={(e) => {
                    if (e.key === "ArrowDown") { e.preventDefault(); setClientIdx((i) => Math.min(i + 1, clientMatches.length - 1)); }
                    if (e.key === "ArrowUp") { e.preventDefault(); setClientIdx((i) => Math.max(i - 1, 0)); }
                    if (e.key === "Enter" && clientMatches[clientIdx]) { e.preventDefault(); pickClient(clientMatches[clientIdx]); }
                  }}
                  role="combobox" aria-expanded={clientMatches.length > 0} aria-controls="ws-client-list" />
                {client && !clientQ && <span className="pill-tag brand ws-client-chosen">{client.name}
                  <button type="button" aria-label="Clear client" onClick={() => setClient(null)}><i className="fas fa-xmark" /></button></span>}
                {clientMatches.length > 0 && (
                  <ul id="ws-client-list" role="listbox" className="ws-client-list">
                    {clientMatches.map((c, i) => (
                      <li key={c.id} role="option" aria-selected={i === clientIdx} className={i === clientIdx ? "on" : ""}
                        onMouseDown={(e) => { e.preventDefault(); pickClient(c); }}>{c.name}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            <div className="seg" role="group" aria-label="Payment method" style={{ marginTop: ".8rem" }}>
              {PAYMENT_METHODS.map(([v, l]) => <button key={v} type="button" aria-pressed={method === v} onClick={() => setMethod(v)}>{l}</button>)}
            </div>
          </section>

          <section className="panel ws-box">
            <h2>Package type</h2>
            <div className="ws-pkgs">
              {(boot?.packages || []).map((p) => (
                <button key={p.id} type="button" className={`ws-pkg ${pkg?.id === p.id ? "on" : ""}`} onClick={() => selectPackage(p)} aria-pressed={pkg?.id === p.id}>
                  <i className={`fas ${pkgIcon(p.cap)}`} />
                  <span className="nm">{p.name}</span>
                  <span className="cap">{p.cap} bottles</span>
                  {pkg?.id === p.id && avgPkgPrice(p.id) > 0 && <span className="avg mono">avg {n2(avgPkgPrice(p.id))}</span>}
                </button>
              ))}
            </div>
            {pkg && (
              <div className="ws-fill">
                <div className="ws-fill-bar"><div className={fill} style={{ width: `${Math.min((totalQty / cap) * 100, 100)}%` }} /></div>
                <span className={`ws-fill-text ${fill}`}>{fillText}</span>
              </div>
            )}
          </section>

          <section className="panel pos-products" aria-label="Products">
            <div className="pos-search">
              <div className="box">
                <i className="fas fa-magnifying-glass" />
                <input ref={searchRef} value={search} onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search beverages by name, manufacturer or type" aria-label="Search beverages"
                  className={flash ? "flash-err" : ""} autoComplete="off" />
                {search && <button type="button" className="clear" aria-label="Clear search" onClick={() => setSearch("")}><i className="fas fa-xmark" /></button>}
              </div>
              <div className="meta">{flash ? <span className="err" role="alert">{flash}</span>
                : !pkg ? <span>Select a package type, then click products to fill it.</span>
                : <span>{search ? `${filtered.length} match${filtered.length === 1 ? "" : "es"}` : "Click a product to add it to the current package"}</span>}</div>
            </div>
            <div className="pos-cards">
              {filtered.map((p) => {
                const priced = !pkg || !!getPrice(p.id, pkg.id);
                const inCart = cart.find((i) => i.id === p.id)?.qty || 0;
                return (
                  <button key={p.id} type="button" className={`pcard ${inCart ? "in-cart" : ""}`} disabled={p.stock <= 0} onClick={() => addToCart(p)}>
                    <span className="nm">{p.name}</span>
                    <span className="mf">{p.mfr || "No manufacturer"}{p.type ? ` · ${p.type}` : ""}</span>
                    <span className="ws-chips">
                      {(boot?.packages || []).map((pg) => {
                        const pp = getPrice(p.id, pg.id);
                        return pp ? <span key={pg.id} className={`ws-chip ${pkg?.id === pg.id ? "on" : ""}`}>{pg.cap}: {n2(pp.package_price)}</span> : null;
                      })}
                    </span>
                    <span className="row">
                      {!priced ? <span className="st out">No price for this package</span>
                        : <span className={`st ${p.stock <= 0 ? "out" : p.stock < 5 ? "low" : "ok"}`}>{p.stock <= 0 ? "Out of stock" : `${p.stock} in stock`}</span>}
                      {inCart > 0 && <span className="ws-incart mono">×{inCart}</span>}
                    </span>
                  </button>
                );
              })}
              {boot && filtered.length === 0 && <div className="pos-empty"><i className="fas fa-magnifying-glass" />No beverages match “{search}”.</div>}
            </div>
          </section>
        </div>

        {/* ── right: package summary & checkout ── */}
        <section className="panel pos-cart" aria-label="Package summary">
          <div className="hd"><h2>Package summary</h2><span className="pill">{totalQty}{pkg ? `/${cap}` : ""} bottles</span>
            <button type="button" className="linkbtn" style={{ marginLeft: "auto" }} onClick={clearCart} disabled={!cart.length}>Clear</button></div>
          <div className="pos-lines">
            {cart.length === 0 ? <div className="cart-empty"><i className="fas fa-box-open" />The package is empty.<br /><small>{pkg ? "Click products to add them." : "Select a package type first."}</small></div>
              : cart.map((item, idx) => (
                <div key={item.id} className="pos-line">
                  <div className="top">
                    <div style={{ minWidth: 0 }}><div className="nm">{item.name}</div>
                      <div className="sub mono">{n2(item.package_price)} per package · {n2(item.price_per_bottle)} per bottle</div></div>
                    <div><div className="lt mono">{n2(item.qty * item.price_per_bottle)}</div>
                      <button type="button" className="rm" onClick={() => setCart(cart.filter((_, k) => k !== idx))}>Remove</button></div>
                  </div>
                  <div className="ctl"><span className="qty">
                    <button type="button" aria-label={`One less ${item.name}`} onClick={() => dec(idx)}>−</button>
                    <span className="mono">{item.qty}</span>
                    <button type="button" aria-label={`One more ${item.name}`} onClick={() => inc(idx)}>+</button></span></div>
                </div>
              ))}
          </div>
          <div className="pos-pay">
            <div className="sums"><div className="grand"><span>Total ({totalQty} bottles)</span><span className="mono">{n2(total)}</span></div></div>
            <div className="recv"><label htmlFor="ws-recv">Amount received (TZS)</label>
              <input id="ws-recv" type="number" min="0" step="0.01" inputMode="decimal" placeholder="0" value={received}
                onChange={(e) => setReceived(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); completeSale(); } }} /></div>
            {validation && <div className="chg short">{validation}</div>}
            {!validation && cart.length > 0 && receivedNum >= total && total > 0 && (
              <div className="chg ok" role="status"><span>Change: <strong className="mono">{n2(receivedNum - total)}</strong></span>
                {countdown != null && <small>Paying in {countdown}s · Esc to stop</small>}</div>
            )}
            <div className="pos-actions">
              <button type="button" className="btn-pay" disabled={!canCheckout} onClick={completeSale}>{submitting ? "Processing…" : "Complete sale"} <kbd style={{ background: "rgba(255,255,255,.2)", color: "#fff" }}>F2</kbd></button>
              <button type="button" className="btn" onClick={openPreview}>Preview</button>
            </div>
            <div className="pos-actions">
              <button type="button" className="btn" style={{ flex: 1 }} onClick={saveDraft} disabled={!cart.length}><i className="fas fa-floppy-disk" /> Save draft</button>
              <button type="button" className="btn" style={{ flex: 1 }} onClick={cancelSale}>Cancel sale</button>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}
